import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Divider,
  IconButton,
  LoadingIcon,
  Modal,
  ProductSelectCard,
  Typography,
  useToast,
} from '@/primer/components';
import { PageColumn } from '@/components/layout';
import { OptOutConfirmationModal, ProductDetailsSheet } from '@/components/ui';
import { useCampaign, useReserveProduct, useUnreserveProduct } from '@/api';
import { useReservationExpiry } from '@/hooks/useReservationTimer';
import type { CampaignProduct } from '@bvengineering/cas-member-gateway-service-client';
import { useIsProviderConnected } from '@/api/queries/settings/useQuerySocialConnections';
import { SocialProvider } from '@/api/queries/settings/constants';
import { MS_PER_DAY, findReservedUserProduct, variantInStock } from '@/utils';

const productInStock = (p: CampaignProduct): boolean => {
  if (p.variants && p.variants.length > 0) {
    return p.variants.some(variantInStock);
  }
  return (p.remainingSampleCount ?? 0) > 0;
};

// ─── Component ────────────────────────────────────────────────────────────────

/** Select Product wizard step: pick a product/variant and reserve it. */
export function SelectProduct() {
  const { id: campaignId } = useParams({ from: '/_authenticated/campaign/$id/select-product' });
  const navigate = useNavigate();

  const { data: campaign, isLoading, error } = useCampaign(campaignId);
  const reserveMutation = useReserveProduct();
  const unreserveMutation = useUnreserveProduct();
  const { username: instagramUsername } = useIsProviderConnected(SocialProvider.INSTAGRAM);
  const { showToast } = useToast();

  const [detailsProduct, setDetailsProduct] = useState<CampaignProduct | null>(null);
  const [draftVariantGtin, setDraftVariantGtin] = useState<string | undefined>(undefined);
  const [selectedProduct, setSelectedProduct] = useState<CampaignProduct | null>(null);
  const [selectedVariantGtin, setSelectedVariantGtin] = useState<string | undefined>(undefined);
  const [reserveError, setReserveError] = useState('');
  const [showOptOutConfirm, setShowOptOutConfirm] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  const isParticipating = campaign?.state?.toLowerCase() === 'participating';
  const reservedUserProduct = findReservedUserProduct(campaign?.userProducts);
  const userReservedProductId = reservedUserProduct?.casProductId ?? null;

  const reservedDate = reservedUserProduct?.reservedDate ?? '';
  const hasExpiredTimer = useReservationExpiry(reservedDate);
  const handledExpiryRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasExpiredTimer || !userReservedProductId || handledExpiryRef.current === reservedDate) {
      return;
    }
    handledExpiryRef.current = reservedDate;
    unreserveMutation.mutate({ campaignId, casProductId: userReservedProductId });
    setShowExpiredModal(true);
  }, [campaignId, hasExpiredTimer, reservedDate, unreserveMutation, userReservedProductId]);

  const handleExpiredDismiss = () => setShowExpiredModal(false);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingIcon size="xl" />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error || !campaign) {
    return (
      <PageColumn className="text-center py-8">
        <div className="flex flex-col items-center gap-4">
          <Typography variant="header" size="h1" color="error" as="h1">
            Campaign Not Found
          </Typography>
          <Button onClick={() => navigate({ to: '/dashboard' })}>Back to Dashboard</Button>
        </div>
      </PageColumn>
    );
  }

  // ── Derived display values ─────────────────────────────────────────────────

  const products = campaign.products ?? [];
  const reservedCount = userReservedProductId ? 1 : 0;
  const selectCount = (campaign.remainingUserSampleCount ?? 1) + reservedCount;
  // Opt-out is disabled once claimed (product-level only; user stays PARTICIPATING).
  const hasClaimed = (campaign.userProducts ?? []).some(
    (product) => !!product.claimedDate || product.state?.toLowerCase() === 'claimed'
  );

  const daysLeft = campaign.expired
    ? Math.max(0, Math.ceil((new Date(campaign.expired).getTime() - Date.now()) / MS_PER_DAY))
    : null;
  const daysLeftLabel =
    daysLeft != null ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left` : null;

  // ── Event handlers ─────────────────────────────────────────────────────────

  const openDetails = (product: CampaignProduct) => {
    // Pre-select the variant if this product is already the confirmed selection.
    setDraftVariantGtin(
      selectedProduct?.casProductId === product.casProductId ? selectedVariantGtin : undefined
    );
    setDetailsProduct(product);
  };

  const closeDetails = () => setDetailsProduct(null);

  const hasVariants = (detailsProduct?.variants?.length ?? 0) > 0;
  const selectDisabled =
    !detailsProduct || !productInStock(detailsProduct) || (hasVariants && !draftVariantGtin);

  const confirmSelect = () => {
    if (!detailsProduct || selectDisabled) return;
    setSelectedProduct(detailsProduct);
    setSelectedVariantGtin(hasVariants ? draftVariantGtin : undefined);
    setReserveError('');
    closeDetails();
  };

  const unconfirmSelect = async () => {
    if (!detailsProduct) return;
    try {
      await unreserveMutation.mutateAsync({
        campaignId,
        casProductId: detailsProduct.casProductId,
      });
      setSelectedProduct(null);
      closeDetails();
    } catch (err) {
      console.error(err);
      showToast({ variant: 'error', message: 'Failed to unselect. Please try again.' });
    }
  };

  const handleNext = async () => {
    if (!selectedProduct && !userReservedProductId) return;
    setReserveError('');

    if (!selectedProduct) {
      navigate({ to: '/campaign/$id/tasks', params: { id: campaignId } });
      return;
    }

    try {
      await reserveMutation.mutateAsync({
        campaignId,
        casProductId: selectedProduct.casProductId,
        reserveProductRequest: { variantGtin: selectedVariantGtin },
      });
      navigate({ to: '/campaign/$id/tasks', params: { id: campaignId } });
    } catch (err) {
      const apiMessage = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setReserveError(
        apiMessage ??
          "We couldn't reserve this product. It may no longer be available — please pick another."
      );
    }
  };

  const isProductSelected = (product: CampaignProduct): boolean =>
    selectedProduct?.casProductId === product.casProductId ||
    (isParticipating && !selectedProduct && userReservedProductId === product.casProductId);

  const handleProductClick = (product: CampaignProduct) => {
    const isCurrentlyReserved =
      isParticipating &&
      (selectedProduct?.casProductId === product.casProductId ||
        userReservedProductId === product.casProductId);

    // If already reserved, open details to show Unselect option
    if (isCurrentlyReserved) {
      openDetails(product);
      return;
    }

    // If a different product is reserved, block selection until current is unselected
    if (
      isParticipating &&
      userReservedProductId &&
      userReservedProductId !== product.casProductId
    ) {
      showToast({
        variant: 'warning',
        message: 'Unselect your current product before choosing another.',
      });
      return;
    }

    openDetails(product);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageColumn className="py-5 flex flex-col gap-6">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <IconButton
          icon="chevron-left"
          onClick={() => navigate({ to: '/dashboard' })}
          aria-label="Go back"
        />
        <Typography variant="header" size="h1" as="h1">
          {campaign.name ?? 'Your campaign'}
        </Typography>
      </div>

      <Divider />

      {/* ── Proposal ────────────────────────────────────────────────────── */}
      <ul className="flex flex-col gap-3 list-none p-0">
        <li className="flex gap-2 items-start">
          <Typography variant="body" size="medium" as="span">
            &bull;
          </Typography>
          <Typography variant="body" size="medium" as="span">
            We'll ship this product to you{' '}
            <Typography
              variant="body"
              size="medium"
              as="strong"
              decoration={{ bold: true, underline: true }}
            >
              for free
            </Typography>
            .
          </Typography>
        </li>
        {instagramUsername && (
          <li className="flex gap-2 items-start">
            <Typography variant="body" size="medium" as="span">
              &bull;
            </Typography>
            <Typography variant="body" size="medium" as="span">
              You'll post a review of this product on this Instagram account:{' '}
              <Typography
                variant="body"
                size="medium"
                as="strong"
                decoration={{ bold: true, underline: true }}
              >
                {instagramUsername}
              </Typography>
              .
            </Typography>
          </li>
        )}
      </ul>

      {/* ── Product selection ────────────────────────────────────────────── */}
      <Typography variant="header" size="h2" as="h2">
        Select {selectCount} product(s)
      </Typography>

      {products.length === 0 ? (
        <div className="flex flex-col items-center gap-2 text-center py-8">
          <Typography variant="header" size="h3" as="h3">
            No products available
          </Typography>
          <Typography variant="body" size="medium" color="bodylighter">
            There are no products to select for this campaign right now. Please check back later.
          </Typography>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {products.map((product) => {
            const isSelected = isProductSelected(product);
            const inStock = productInStock(product) || isSelected;
            const state = isSelected ? 'selected' : inStock ? 'default' : 'outOfStock';
            return (
              <ProductSelectCard
                key={product.casProductId}
                productName={product.productName ?? 'Product'}
                brandName={product.productBrand ?? undefined}
                imageUrl={product.productImageUrl}
                badgeLabel={daysLeftLabel ?? undefined}
                state={state}
                onClick={() => handleProductClick(product)}
              />
            );
          })}
        </div>
      )}

      {reserveError && (
        <Typography variant="body" size="small" color="error">
          {reserveError}
        </Typography>
      )}

      {/* ── Action bar ──────────────────────────────────────────────────── */}
      {products.length > 0 && (
        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="large"
            fullWidth
            onClick={() => setShowOptOutConfirm(true)}
            disabled={hasClaimed}
          >
            Opt out
          </Button>
          <Button
            variant="primary"
            size="large"
            fullWidth
            onClick={handleNext}
            disabled={!selectedProduct && !userReservedProductId}
            isLoading={reserveMutation.isPending || unreserveMutation.isPending}
          >
            Next
          </Button>
        </div>
      )}

      {/* ── Product details sheet ──────────────────────────────────────────── */}
      <ProductDetailsSheet
        product={detailsProduct}
        isReserved={
          isParticipating &&
          userReservedProductId === detailsProduct?.casProductId &&
          !reservedUserProduct?.claimedDate
        }
        draftVariantGtin={draftVariantGtin}
        onVariantChange={setDraftVariantGtin}
        selectDisabled={selectDisabled}
        isUnreserving={unreserveMutation.isPending}
        onClose={closeDetails}
        onSelect={confirmSelect}
        onUnselect={unconfirmSelect}
      />

      {/* ── Opt-out confirmation ───────────────────────────────────────────── */}
      <OptOutConfirmationModal
        isOpen={showOptOutConfirm}
        onClose={() => setShowOptOutConfirm(false)}
        campaignId={campaignId}
      />

      {/* ── Reservation expired ────────────────────────────────────────────── */}
      <Modal
        isOpen={showExpiredModal}
        onClose={handleExpiredDismiss}
        icon="circle-info-filled"
        title="Time is up! Product(s) are no longer reserved"
        description="Due to the lapsed timer, you will need to reserve the products again."
        primaryButton={{ label: 'Got it', onClick: handleExpiredDismiss }}
      />
    </PageColumn>
  );
}

export default SelectProduct;
